import { Controller } from "@hotwired/stimulus"
import L from "leaflet"
import "leaflet.markercluster"
import { trackPagePulse } from "../global/page_pulse"

import "leaflet.markercluster/dist/MarkerCluster.css"
import "./markercluster.css"

function isHidden(mapContainer) {
    return mapContainer.classList.contains('hidden');
}

export default class extends Controller {
    static targets = ["map", "mapContainer", "resultsContainer", "mapChooser", "listChooser"];
    static values = {
        geo: Array,
    };

    connect() {
        if (!this.geoValue || this.geoValue.length === 0) {
            const currentView = sessionStorage.getItem("search_view") || "list";

            this.resultsContainerTarget.classList.remove('hidden');
            this.mapContainerTarget.classList.add('hidden');

            if (currentView === "map") {
                this.mapChooserTarget.classList.add('hidden');
                this.listChooserTarget.classList.remove('hidden');
            } else {
                this.mapChooserTarget.classList.remove('hidden');
                this.listChooserTarget.classList.add('hidden');
            }

            return;
        }

        const markers = L.markerClusterGroup({
            showCoverageOnHover: false,
            spiderfyOnMaxZoom: false,
            disableClusteringAtZoom: 14,
            iconCreateFunction: function(cluster) {
                const childCount = cluster.getChildCount();
                const radius = 40 + (childCount / 10); // Increase the radius here
                return L.divIcon({
                    html: `<div class="marker-cluster-custom" style="width: ${radius}px; height: ${radius}px; border-radius: 50%;"><span>${childCount}</span></div>`,
                    className: 'cluster-icon',
                    iconSize: L.point(radius, radius)
                });
            }
        });

        for (const geo of this.geoValue) {
            const marker = this.createMarker(geo);
            markers.addLayer(marker);
        }

        this.initMap(markers);
        if (sessionStorage.getItem("search_view") === "list" || !sessionStorage.getItem("search_view")) {
            this.showList();
        } else if (sessionStorage.getItem("search_view") === "map") {
            this.showMap();
        }
    }

    initMap(markers) {
        this.map = L.map('map',
            { draggable: true, dragging: true, scrollWheelZoom: true, touchZoom: true, doubleClickZoom: false, boxZoom: false, zoomControl: true, maxZoom: 16, minZoom: 6, zoomSnap: 0.5, zoomDelta: 0.5, worldCopyJump: true, maxBoundsViscosity: 1.0, maxBounds: [[-90, -180], [90, 180]] });
        L.tileLayer('https://api.mapbox.com/styles/v1/wsch/clgdrdbpc003t01rxot9ftwyc/tiles/256/{z}/{x}/{y}?access_token=pk.eyJ1Ijoid3NjaCIsImEiOiJja3Z0bGZ2ZjI1MW1pMm5udTkwN25zanUyIn0.DTskTE-90l5QY7-2Y86L6Q', {
            attribution: `© <a href="https://www.mapbox.com/about/maps/">Mapbox</a> © <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a> <strong><a href="https://www.mapbox.com/map-feedback/" target="_blank" rel="noopener noreferrer">Improve this map</a></strong>`,
            maxZoom: 18
        }).addTo(this.map);

        if (L.Browser.mobile) {
            this.map.dragging.disable();
            this.map.touchZoom.enable();
            this.map.doubleClickZoom.disable();
            this.map.scrollWheelZoom.disable();
            this.map.boxZoom.disable();
            this.map.keyboard.disable();
            if (this.map.tap) this.map.tap.disable();
        } else {
            this.map.dragging.enable();
            this.map.touchZoom.enable();
            this.map.doubleClickZoom.enable();
            this.map.scrollWheelZoom.enable();
            this.map.boxZoom.enable();
            this.map.keyboard.enable();
            if (this.map.tap) this.map.tap.enable();
        }

        this.map.attributionControl.setPrefix();

        //this.group = L.featureGroup(markers);
        //this.group.addTo(this.map);
        this.map.addLayer(markers);

        this.bounds = markers.getBounds();

        //this.map.fitBounds(this.group.getBounds(), { padding: [20, 20] });
        this.map.fitBounds(this.bounds, { padding: [20, 20] });

        /*window.addEventListener('resize', () => {
            this.map.fitBounds(this.bounds, { padding: [20, 20] });
            if (window.innerWidth >= 1024) {
                window.scrollTo(0, 0);
            }
        });*/

        this.map.whenReady(() => {
            const logo = `<svg width="88" height="23" viewBox="0 0 88 23" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" fill-rule="evenodd"><defs><path id="logo" d="M11.5 2.25c5.105 0 9.25 4.145 9.25 9.25s-4.145 9.25-9.25 9.25-9.25-4.145-9.25-9.25 4.145-9.25 9.25-9.25zM6.997 15.983c-.051-.338-.828-5.802 2.233-8.873a4.395 4.395 0 013.13-1.28c1.27 0 2.49.51 3.39 1.42.91.9 1.42 2.12 1.42 3.39 0 1.18-.449 2.301-1.28 3.13C12.72 16.93 7 16 7 16l-.003-.017zM15.3 10.5l-2 .8-.8 2-.8-2-2-.8 2-.8.8-2 .8 2 2 .8z"/><path id="text" d="M50.63 8c.13 0 .23.1.23.23V9c.7-.76 1.7-1.18 2.73-1.18 2.17 0 3.95 1.85 3.95 4.17s-1.77 4.19-3.94 4.19c-1.04 0-2.03-.43-2.74-1.18v3.77c0 .13-.1.23-.23.23h-1.4c-.13 0-.23-.1-.23-.23V8.23c0-.12.1-.23.23-.23h1.4zm-3.86.01c.01 0 .01 0 .01-.01.13 0 .22.1.22.22v7.55c0 .12-.1.23-.23.23h-1.4c-.13 0-.23-.1-.23-.23V15c-.7.76-1.69 1.19-2.73 1.19-2.17 0-3.94-1.87-3.94-4.19 0-2.32 1.77-4.19 3.94-4.19 1.03 0 2.02.43 2.73 1.18v-.75c0-.12.1-.23.23-.23h1.4zm26.375-.19a4.24 4.24 0 00-4.16 3.29c-.13.59-.13 1.19 0 1.77a4.233 4.233 0 004.17 3.3c2.35 0 4.26-1.87 4.26-4.19 0-2.32-1.9-4.17-4.27-4.17zM60.63 5c.13 0 .23.1.23.23v3.76c.7-.76 1.7-1.18 2.73-1.18 1.88 0 3.45 1.4 3.84 3.28.13.59.13 1.2 0 1.8-.39 1.88-1.96 3.29-3.84 3.29-1.03 0-2.02-.43-2.73-1.18v.77c0 .12-.1.23-.23.23h-1.4c-.13 0-.23-.1-.23-.23V5.23c0-.12.1-.23.23-.23h1.4zm-34 11h-1.4c-.13 0-.23-.11-.23-.23V8.22c.01-.13.1-.22.23-.22h1.4c.13 0 .22.11.23.22v.68c.5-.68 1.3-1.09 2.16-1.1h.03c1.09 0 2.09.6 2.6 1.55.45-.95 1.4-1.55 2.44-1.56 1.62 0 2.93 1.25 2.9 2.78l.03 5.2c0 .13-.1.23-.23.23h-1.41c-.13 0-.23-.11-.23-.23v-4.59c0-.98-.74-1.71-1.62-1.71-.8 0-1.46.7-1.59 1.62l.01 4.68c0 .13-.11.23-.23.23h-1.41c-.13 0-.23-.11-.23-.23v-4.59c0-.98-.74-1.71-1.62-1.71-.85 0-1.54.79-1.6 1.8v4.5c0 .13-.1.23-.23.23zm53.615 0h-1.61c-.04 0-.08-.01-.12-.03-.09-.06-.13-.19-.06-.28l2.43-3.71-2.39-3.65a.213.213 0 01-.03-.12c0-.12.09-.21.21-.21h1.61c.13 0 .24.06.3.17l1.41 2.37 1.4-2.37a.34.34 0 01.3-.17h1.6c.04 0 .08.01.12.03.09.06.13.19.06.28l-2.37 3.65 2.43 3.7c0 .05.01.09.01.13 0 .12-.09.21-.21.21h-1.61c-.13 0-.24-.06-.3-.17l-1.44-2.42-1.44 2.42a.34.34 0 01-.3.17zm-7.12-1.49c-1.33 0-2.42-1.12-2.42-2.51 0-1.39 1.08-2.52 2.42-2.52 1.33 0 2.42 1.12 2.42 2.51 0 1.39-1.08 2.51-2.42 2.52zm-19.865 0c-1.32 0-2.39-1.11-2.42-2.48v-.07c.02-1.38 1.09-2.49 2.4-2.49 1.32 0 2.41 1.12 2.41 2.51 0 1.39-1.07 2.52-2.39 2.53zm-8.11-2.48c-.01 1.37-1.09 2.47-2.41 2.47s-2.42-1.12-2.42-2.51c0-1.39 1.08-2.52 2.4-2.52 1.33 0 2.39 1.11 2.41 2.48l.02.08zm18.12 2.47c-1.32 0-2.39-1.11-2.41-2.48v-.06c.02-1.38 1.09-2.48 2.41-2.48s2.42 1.12 2.42 2.51c0 1.39-1.09 2.51-2.42 2.51z"/></defs><mask id="clip"><rect x="0" y="0" width="100%" height="100%" fill="white"/></mask><g id="outline" opacity="0.3" stroke="#000" stroke-width="3"><circle mask="url(#clip)" cx="11.5" cy="11.5" r="9.25"/><use xlink:href="#text" mask="url(#clip)"/></g><g id="fill" opacity="0.9" fill="#fff"><use xlink:href="#logo"/><use xlink:href="#text"/></g></svg>`;
            const node = document.createElement('div');
            node.style = 'position: absolute; bottom: -12px; left: -12px; z-index: 999; padding: 1rem;';
            node.innerHTML = logo;
            document.getElementById('searchmap__container').appendChild(node);
        });

        this.map.on('zoomend', () => {
            const zoomLevel = this.map.getZoom();
        });
    }

    createMarker(lngLat) {

        const name = lngLat.name.trim();
        const url = lngLat.url.trim();
        const img = lngLat.img.trim();
        const id = lngLat.id;
        let popupImpressionTracked = false;

        const marker = L.marker([lngLat.lat, lngLat.lng], { draggable: false, autoPan: true, zoomControl: false })

        const divNode = document.createElement('div');
        divNode.style = 'width: 200px;';

        marker.bindPopup(divNode, { closeOnClick: true, autoClose: true, autoPan: true, closeButton: false });

        marker.on('popupopen', (e) => {
            if (!popupImpressionTracked) {
                popupImpressionTracked = true;
                trackPagePulse("impression", {
                    url,
                    subjectType: "location",
                    subjectId: id,
                });
            }

            document.getElementsByClassName('leaflet-control-zoom')[0].classList.add('hidden', 'lg:block');

            const imgNode = document.createElement('img');
            const link = document.createElement('a');
            const nameDiv = document.createElement('div');

            const localityDiv = document.createElement('div');
            const maxCapacityDiv = document.createElement('div');
            const areaDiv = document.createElement('div');

            const gridDiv = document.createElement('div');

            nameDiv.innerText = name;
            nameDiv.classList.add('ml-1', 'text-mongreen', 'font-bold', 'whitespace-nowrap', 'overflow-hidden', 'text-ellipsis', 'mt-1', 'text-left', 'px-1');

            link.href = url;

            link.target = '_blank';

            imgNode.src = img;
            imgNode.classList.add('object-cover');
            imgNode.alt = name;

            link.appendChild(imgNode);
            link.appendChild(nameDiv);

            divNode.appendChild(link);


            gridDiv.classList.add('ml-1', 'mt-2');

            let dynHtml = `
            <div class="mt-1 text-nepal-800 whitespace-nowrap overflow-hidden text-ellipsis"><i class="fa-solid fa-location-dot" style="width: 20px"></i>
                <span class="text-mongreen font-normal" style="margin-left: 4px;">${lngLat.locality_line}</span>
            </div>
            <div class="grid grid-cols-2 mt-1 whitespace-nowrap overflow-hidden text-ellipsis">
                <div class="text-nepal-800"><i class="fa-solid fa-users" style="width: 20px"></i>
                    <span class="text-mongreen font-normal ml-1">${lngLat.max_capacity}</span>
                </div>

                <div class="text-nepal-800 whitespace-nowrap overflow-hidden text-ellipsis"><i class="fa-solid fa-vector-square" style="width: 20px"></i>
                    <span class="text-mongreen font-normal ml-1">${lngLat.exhibition_area} m²</span>
                </div>
            </div>`;

            gridDiv.innerHTML = dynHtml;
            divNode.appendChild(gridDiv);
        });

        marker.on('popupclose', function (e) {
            document.getElementsByClassName('leaflet-control-zoom')[0].classList.remove('hidden', 'lg:block');
            divNode.innerHTML = '';
        });

        return marker;
    }

    closeAllPopups() {
        this.map.eachLayer(function (layer) {
            if (layer instanceof L.Marker) {
                layer.closePopup();
            }
        });
    }

    showList() {
        this.mapContainerTarget.classList.add('hidden');
        this.resultsContainerTarget.classList.remove('hidden');
        this.mapChooserTarget.classList.remove('hidden');
        this.listChooserTarget.classList.add('hidden');
        this.closeAllPopups();
        window.scrollTo(0, 0);

    }

    showMap() {
        if (!this.map) {
            return;
        }

        this.mapContainerTarget.classList.remove('hidden');
        this.resultsContainerTarget.classList.add('hidden');
        this.mapChooserTarget.classList.add('hidden');
        this.listChooserTarget.classList.remove('hidden');
        this.map.invalidateSize();

        if (this.bounds && this.bounds.isValid()) {
            this.map.fitBounds(this.bounds, { padding: [20, 20] });
        }

        window.scrollTo(0, 0);
    }

    toggleMap() {
        if (!this.map) {
            const currentView = sessionStorage.getItem("search_view") || "list";
            const nextView = currentView === "map" ? "list" : "map";

            sessionStorage.setItem("search_view", nextView);

            if (nextView === "map") {
                this.resultsContainerTarget.classList.remove('hidden');
                this.mapContainerTarget.classList.add('hidden');
                this.mapChooserTarget.classList.add('hidden');
                this.listChooserTarget.classList.remove('hidden');
            } else {
                this.resultsContainerTarget.classList.remove('hidden');
                this.mapContainerTarget.classList.add('hidden');
                this.mapChooserTarget.classList.remove('hidden');
                this.listChooserTarget.classList.add('hidden');
            }

            return;
        }

        if (this.mapContainerTarget.classList.contains('hidden')) {
            this.showMap();
            sessionStorage.setItem("search_view", "map");

        } else {
            this.showList();
            sessionStorage.setItem("search_view", "list");
        }
    }
}
